import $ from 'jquery';
import eventBus from 'EventBus';
import themeService from 'ThemeService';
import focusController  from 'FocusController';
import pageSyncService  from 'PageSyncService';
import util from 'util/Util';
import { pickerCfg } from 'pickerCfg';
import undoService from 'UndoService';
import BasePropertyView from 'BasePropertyView';
import formatTemplate from 'properties/theme/inputs.ejs';
import Immutable        from 'immutable';
import fontService from 'FontService';

class InputsView extends BasePropertyView {

    initialize(opts) {
        super.initialize(opts);

        this.options = opts || {};
        this.template = formatTemplate;
        this.tagName = 'div';
        this.id = opts.el;
        this.inputKeys = ['control_text_input','control_multiple_text_input','control_date_input','control_time_input','control_numeric_input']
        this.selectedColor = 'lightgrey';
        this.unselectedColor = 'transparent';

        this.initializeTextAttributes();
        this.initializeBorderAttributes();
        this.model.set('inputBgColor', themeService.resolve('~inputBgColor'), { silent: true });

        this.events = {
            'change input[type=text]': 'updateAttributes',
            'change input[type=color]': 'updateColorAttributes',
            'blur input[type=text]': 'lostFocus',
            'focus input[type=text]': 'getFocus',
            'change select': 'updateSelectAttributes',
            'click .styleSelectorInput': 'updateTextStyleAttributes',
            'click .weightSelectorInput': 'updateTextWeightAttributes'
        };

        this.listenTo(this.model, 'change', this.render);

        // Don't change this name without knowing that the base class may assume this name
        this.dropdownPropNames = Immutable.List(
            ['inputFontSize', 'inputFont', 'inputBorderStyle', 'inputBorderWidth',
                'inputBorderRadius']);

        return this;
    }

    initializeTextAttributes() {
        this.model.set('inputTextColor', themeService.resolve('~inputTextColor'), { silent: true });
        var fontProp = themeService.resolve('~inputFont').split(',');

        this.model.set('inputFont', fontProp[0].trim(), { silent: true });
        this.model.set('inputFontSize', fontProp[1].trim(), { silent: true });

        if ((fontProp[2] && fontProp[2].toLowerCase().indexOf('bold') > -1) ||
            (fontProp[3] && fontProp[3].toLowerCase().indexOf('bold') > -1)) {
            this.model.set('inputWeight', 'bold !important', { silent: true });
        } else {
            this.model.set('inputWeight', 'normal !important', { silent: true });
        }
        if (this.model.get('inputWeight') === 'normal !important') {
            this.model.set('inputWeightSelection', this.unselectedColor, { silent: true });
        } else {
            this.model.set('inputWeightSelection', this.selectedColor, { silent: true });
        }

        if ((fontProp[2] && fontProp[2].toLowerCase().indexOf('italic') > -1) ||
            (fontProp[3] && fontProp[3].toLowerCase().indexOf('italic') > -1)) {
            this.model.set('inputStyle', 'italic !important', { silent: true });
        } else {
            this.model.set('inputStyle', 'normal !important', { silent: true });
        }
        if (this.model.get('inputStyle') === 'normal !important') {
            this.model.set('inputStyleSelection', this.unselectedColor, { silent: true });
        } else {
            this.model.set('inputStyleSelection', this.selectedColor, { silent: true });
        }
    }

    initializeBorderAttributes() {
        this.model.set('inputBorderColor', themeService.resolve('~inputBorderColor'), { silent: true });
        var borderProp = themeService.resolve('~inputBorder').split(',');

        this.model.set('inputBorderWidth', borderProp[0].trim(), { silent: true });
        this.model.set('inputBorderStyle', borderProp[1].trim(), { silent: true });
        this.model.set('inputBorderRadius', borderProp[2] ? borderProp[2].trim() : '0', { silent: true });
    }

    updateThemeFontAttributes() {
        const themePropArray = [this.model.get('inputFont'), this.model.get('inputFontSize'), this.model.get('inputWeight'), this.model.get('inputStyle')];
        themeService._theme['~inputFont'] = themePropArray.join(',');
    }

    updateThemeBorderAttributes() {
        const themePropArray = [this.model.get('inputBorderWidth'), this.model.get('inputBorderStyle'), this.model.get('inputBorderRadius')];
        themeService._theme['~inputBorder'] = themePropArray.join(',');
    }

    updateTextStyleAttributes(ev) {
        ev.preventDefault();
        const propName = ev.currentTarget.name;
        let oldValue = this.model.get(propName);
        let style = this.model.get(propName);

        if (this.model.get(propName).indexOf('italic') > -1) {
            this.model.set(propName + 'Selection', this.unselectedColor, { silent: true });
            style = 'normal !important';
        } else {
            this.model.set(propName + 'Selection', this.selectedColor, { silent: true });
            style = 'italic !important';
        }

        this.model.set(propName, style, { silent: true });
        this.updateThemeFontAttributes();
        this.render();
        eventBus.trigger('InputThemeChange');

        undoService.add({
            undo: () => {
                if (oldValue.indexOf('italic') > -1) {
                    this.model.set(propName + 'Selection', this.selectedColor, { silent: true });
                } else {
                    this.model.set(propName + 'Selection', this.unselectedColor, { silent: true });
                }
                this.model.set(propName, oldValue, { silent: true });
                this.updateThemeFontAttributes();
                // PF-2041 Sync is lost between properties when an element is recreated via Undo/Redo.
                // Got to switch it for PageSyncService.Please review this method in case that performance issues are being experimented.
                //eventBus.trigger('InputThemeChange');
                //this.model.trigger('change', this.model);
                pageSyncService.renderCurrentPageView();
                focusController.switchPropsViews();
            },
            redo: () => {
                if (style.indexOf('italic') > -1) {
                    this.model.set(propName + 'Selection', this.selectedColor, { silent: true });
                } else {
                    this.model.set(propName + 'Selection', this.unselectedColor, { silent: true });
                }
                this.model.set(propName, style, { silent: true });
                this.updateThemeFontAttributes();

                // PF-2041 Sync is lost between properties when an element is recreated via Undo/Redo.
                // Got to switch it for PageSyncService.Please review this method in case that performance issues are being experimented.
                //eventBus.trigger('InputThemeChange');
                //this.model.trigger('change', this.model);
                pageSyncService.renderCurrentPageView();
                focusController.switchPropsViews();
            }
        })
    }

    updateTextWeightAttributes(ev) {
        ev.preventDefault();
        const propName = ev.currentTarget.name;
        let oldValue = this.model.get(propName);
        let weight = this.model.get(propName);

        if (this.model.get(propName).indexOf('bold') > -1) {
            this.model.set(propName + 'Selection', this.unselectedColor, { silent: true });
            weight = 'normal !important';
        } else {
            this.model.set(propName + 'Selection', this.selectedColor, { silent: true });
            weight = 'bold !important';
        }

        this.model.set(propName, weight, { silent: true });
        this.updateThemeFontAttributes();
        this.render();
        eventBus.trigger('InputThemeChange');

        undoService.add({
            undo: () => {
                if (oldValue.indexOf('bold') > -1) {
                    this.model.set(propName + 'Selection', this.selectedColor, { silent: true });
                } else {
                    this.model.set(propName + 'Selection', this.unselectedColor, { silent: true });
                }
                this.model.set(propName, oldValue, { silent: true });
                this.updateThemeFontAttributes();
                // PF-2041 Sync is lost between properties when an element is recreated via Undo/Redo.
                // Got to switch it for PageSyncService.Please review this method in case that performance issues are being experimented.
                //eventBus.trigger('InputThemeChange');
                //this.model.trigger('change', this.model);
                pageSyncService.renderCurrentPageView();
                focusController.switchPropsViews();
            },
            redo: () => {
                if (weight.indexOf('italic') > -1) {
                    this.model.set(propName + 'Selection', this.selectedColor, { silent: true });
                } else {
                    this.model.set(propName + 'Selection', this.unselectedColor, { silent: true });
                }
                this.model.set(propName, weight, { silent: true });
                this.updateThemeFontAttributes();
                // PF-2041 Lost Sync between properties when an element is recreated via Undo/Redo
                //eventBus.trigger('InputThemeChange');
                //this.model.trigger('change', this.model);
                pageSyncService.renderCurrentPageView();
                focusController.switchPropsViews();
            }
        })
    }

    updateAttributes(ev, name, val) {
        ev.preventDefault();
        const propName = name || ev.currentTarget.name;
        const propVal = val || ev.currentTarget.value;
        const oldPropVal = this.model.get(propName);
        const oldThemeValue=themeService._theme['~' + propName];

        if (util.isValidColorcode(propVal)) {
            let color = this.resolveColor(propVal);
            this.model.set(propName, color,{silent:true});
            themeService._theme['~' + propName] = color;
            this.addUndoRedoColorAction(propName,oldThemeValue,oldPropVal,color);
            this.render();
            $('#' + propName).val(color);
            eventBus.trigger('InputThemeChange');
        }
        else{
          this.render();
        }
    }

    updateColorAttributes(ev) {
        ev.preventDefault();
        const propName = ev.currentTarget.name;
        const propVal = ev.currentTarget.value;
        const oldPropVal = this.model.get(propName);
        const oldThemeValue=themeService._theme['~' + propName];

        let color = this.resolveColor(propVal);
        this.model.set(propName, color,{silent:true});
        themeService._theme['~' + propName] = color;
        this.addUndoRedoColorAction(propName,oldThemeValue,oldPropVal,color);
        this.render();
        $('#' + propName).val(color);
        eventBus.trigger('InputThemeChange');
    }

    addUndoRedoColorAction(propName,oldThemeValue,oldPropVal,currentColor){
        undoService.add({
            undo:() => {
                this.model.set(propName, oldPropVal,{silent:true});
                themeService._theme['~' + propName] = oldThemeValue;
                //eventBus.trigger('InputThemeChange');
                pageSyncService.renderCurrentPageView();
                focusController.switchPropsViews();
                $('#' + propName).val(oldPropVal);
            },
            redo: () => {
                this.model.set(propName, currentColor,{silent:true});
                themeService._theme['~' + propName] = currentColor;
                // PF-2041 Sync is lost between properties when an element is recreated via Undo/Redo.
                // Got to switch it for PageSyncService.Please review this method in case that performance issues are being experimented.
                //eventBus.trigger('InputThemeChange');
                pageSyncService.renderCurrentPageView();
                focusController.switchPropsViews();
                $('#' + propName).val(currentColor);
            }
        })
    }

    updateSelectAttributes(ev, name, val) {
        ev.preventDefault();
        const propName = name || ev.currentTarget.name;
        const propVal = val || ev.currentTarget.value;

        const oldValue = this.model.get(propName);       

        this.model.set(propName, propVal,{silent:true});
        if (propName === 'inputFont' || propName === 'inputFontSize') {
            this.updateThemeFontAttributes();
            themeService.updateInputObjectsFontChanged();
        } else {
            this.updateThemeBorderAttributes();
        }
        this.render();
        eventBus.trigger('InputThemeChange');


        undoService.add({
            undo:() => {
                if (propName === 'inputFont' || propName === 'inputFontSize') {
                    this.model.set(propName, oldValue,{silent:true});
                    this.updateThemeFontAttributes();
                    themeService.updateInputObjectsFontChanged();
                } else {
                    this.model.set(propName, oldValue,{silent:true});
                    this.updateThemeBorderAttributes();
                }
                //eventBus.trigger('InputThemeChange');
                pageSyncService.renderCurrentPageView();
                focusController.switchPropsViews();
            },
            redo: () => {
                if (propName === 'inputFont' || propName === 'inputFontSize') {
                    this.model.set(propName, propVal,{silent:true});
                    this.updateThemeFontAttributes();
                    themeService.updateInputObjectsFontChanged();
                } else {
                    this.model.set(propName, propVal,{silent:true});
                    this.updateThemeBorderAttributes();
                }
                // PF-2041 Sync is lost between properties when an element is recreated via Undo/Redo.
                // Got to switch it for PageSyncService.Please review this method in case that performance issues are being experimented.
                //eventBus.trigger('InputThemeChange');
                pageSyncService.renderCurrentPageView();
                focusController.switchPropsViews();
            }
        })
    }


    render() {
        super.render();
        
        if (this.template) {
            this.$el.html(this.template(this.model.toJSON()));
        }

        util.prepareComboWidget(this.$el, this);

        const fonts = fontService.loadFontsDropDown(false);
        $("#inputFont").append(fonts);
        

        this.createEditableDropDown('inputBorderWidth');
        this.createEditableDropDown('inputBorderRadius');
        this.createEditableDropDown('inputFontSize');

        this.checkBorderDropDown('#inputBorderWidth', 'inputBorderStyle')
        this.checkBorderDropDown('#inputBorderRadius', 'inputBorderStyle')

        //.$('input[type=color]', this.$el).spectrum(pickerCfg);

        this.updateForm();
        this.delegateEvents();
        return this;
    }

}

export default InputsView;
